import React, { useContext, useEffect, useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import ShortUniqueId from 'short-unique-id';

import { CourseOverviewTab, MembersTab, ProjectSettings, SettingsTab } from 'components/organisms/CourseDashboard';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/CourseDashboard/PageProvider';
import withResponsiveLayout from 'hoc/withResponsive';
import courseRequests from 'services/course';
import Mobile from 'pages/CourseDashboard/LTI/mobile';
import Tablet from 'pages/CourseDashboard/LTI/tablet';
import Desktop from 'pages/CourseDashboard/LTI/desktop';
import { AlertDialog, ResponsiveDialog } from 'shell/components';
import SyncRosterDialog from 'components/organisms/Teams/SyncRosterDialog';
import CloningContents from 'components/organisms/CourseDashboard/CloningContents';
import { COURSE_ID_DICTIONARY } from 'utils/helper';

const getActiveCourse = (course) => {
    return course
        ? {
              ...course,
              field_of_study: course?.field_of_study || '',
          }
        : null;
};

const getProject = (course) => {
    return {
        title: course?.project_title || '',
        start_on: course?.start_on || 0,
        end_on: course?.end_on || 0,
    };
};

const PageLayout = () => {
    const { message } = useSnackbar();
    const { auth, selectAccount, startCourseSetup } = useAuth();
    const { accountUsers, groups } = useUser2();
    const { device, width } = useViewport();

    const {
        cloningContentsDialog,
        setCloningContentsDialog,
        dialog,
        setDialog,
        generatedIdList,
        setGeneratedIdList,
        isSyncRosterLoading,
        syncRosterDialog,
        setSyncRosterDialog,
    } = useContext(PageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [courses, setCourses] = useState(null);
    const [activeCourse, setActiveCourse] = useState(null);
    const [courseAccountUsers, setCourseAccountUsers] = useState(null);
    const [projectSettings, setProjectSettings] = useState({ isOpen: false });

    const updateProjectSettings = async (form) => {
        const updatedCourse = { project_title: form.title, start_on: form.start_on, end_on: form.end_on };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({
            ...current,
            project_title: form.title,
            start_on: form.start_on,
            end_on: form.end_on,
        }));
        await courseRequests.updateCourse(activeCourse.course_id, updatedCourse);
        message.success('Project settings updated successfully.');
    };

    const handleGenerateId = async () => {
        const generatedId = new ShortUniqueId({
            length: 10,
            dictionary: COURSE_ID_DICTIONARY,
        })();
        await courseRequests.updateCourse(activeCourse.course_id, { generated_id: generatedId });
        const updatedCourse = { ...activeCourse, generated_id: generatedId };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({
            ...current,
            generated_id: generatedId,
        }));
    };

    const handleProjectCardDropdown = ({ key }) => {
        switch (key) {
            case 'clone-contents': {
                return setCloningContentsDialog((current) => ({
                    ...current,
                    open: true,
                    message: `Delete 'this course'?`,
                    description:
                        'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
                    variant: 'destructive',
                    actionButtonText: 'Delete',
                    onAction: () => {
                        setCloningContentsDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                    },
                }));
            }
            case 'edit-project-details': {
                return setProjectSettings({ isOpen: true });
            }
            default:
                return;
        }
    };

    const updateCourseSettings = async (form) => {
        const updatedCourse = {
            school: form.school,
            faculty: form.faculty,
            name: form.name,
            semester: form.semester,
            section: form.section,
            field_of_study: form.field_of_study,
        };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({ ...current, ...updatedCourse }));
        await courseRequests.updateCourse(auth.course_id, updatedCourse);
        message.success('Course details updated successfully.');
    };

    useEffect(() => {
        const generatedUsers = accountUsers
            ? accountUsers
                  .filter((accountUser) => accountUser.active)
                  .map((accountUser) => {
                      const isPending = Boolean(accountUser.login_activity);
                      return { ...accountUser, is_pending: isPending };
                  })
            : [];
        setCourseAccountUsers(generatedUsers);
    }, [accountUsers]);

    useEffect(() => {
        (async () => {
            const course = await courseRequests.findCourse(auth.course_id);
            if (course) {
                const currentCourse = {
                    user_id: auth.user_id,
                    owner_account_id: auth.id,
                    role: auth.role,
                    course_id: auth.course_id,
                    group_id: auth.group_id,
                    generated_id: course.generated_id || null,
                    school: course.school,
                    faculty: course.faculty,
                    name: course.name,
                    semester: course.semester,
                    field_of_study: course.field_of_study,
                    section: course.section,
                    project_title: course.project_title,
                    start_on: course.start_on,
                    end_on: course.end_on,
                    finished_course_set_up: course.finished_course_set_up !== false,
                    group_formation: course.group_formation || null,
                    survey: course.survey || null,
                    finished_group_generation: course.finished_group_generation || false,
                };

                const courseDraft = await courseRequests.findCourseDraft(auth.course_id);
                const courseData = {
                    ...currentCourse,
                    school: currentCourse.school || courseDraft?.school,
                    faculty: currentCourse.faculty || courseDraft?.faculty,
                    name: currentCourse.name || courseDraft?.course_name,
                    semester: currentCourse.semester || courseDraft?.semester,
                    section: currentCourse.section || courseDraft?.section,
                    field_of_study: currentCourse.field_of_study || courseDraft?.field_of_study,
                    project_title: currentCourse.project_title || courseDraft?.project_title,
                    start_on: currentCourse.start_on || courseDraft?.start_on,
                    end_on: currentCourse.end_on || courseDraft?.end_on,
                };
                setCourses([]);
                setActiveCourse(courseData);
                setIsLoading(false);
            }
        })();
    }, []);

    const tabOptions = [
        {
            key: 'overview',
            value: 'Course Overview',
            content: (
                <CourseOverviewTab
                    device={device}
                    course={activeCourse}
                    role={auth?.role}
                    project={getProject(activeCourse)}
                    numGroups={groups?.length}
                    selectProject={() => {
                        setIsLoading(true);
                        selectAccount(activeCourse.owner_account_id);
                    }}
                    setProjectSettings={setProjectSettings}
                    handleProjectCardDropdown={handleProjectCardDropdown}
                    handleGenerateId={handleGenerateId}
                />
            ),
        },
        {
            key: 'members',
            value: 'Members',
            content: <MembersTab isInCourseDashboard={true} course={activeCourse} accountUsers={courseAccountUsers} />,
        },
        {
            key: 'settings',
            value: 'Settings',
            content: <SettingsTab device={device} course={activeCourse} updateCourseSettings={updateCourseSettings} />,
        },
    ];

    const [tab, setTab] = useState(tabOptions[0].key);

    const props = {
        isLoading,
        courses,
        activeCourse: getActiveCourse(activeCourse),
        setActiveCourse,
        startCourseSetup: () => {
            if (courses.length >= 10) return message.error('You have reached the maximum limit of number of courses.');
            startCourseSetup();
        },

        role: auth?.role,

        project: getProject(activeCourse),
        numGroups: groups?.length,
        selectProject: () => {
            setIsLoading(true);
            selectAccount(activeCourse.owner_account_id);
        },
        setProjectSettings,
        accountUsers,
        updateCourseSettings,
        tab,
        setTab,
        tabOptions,
        handleProjectCardDropdown,
        handleGenerateId,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const projectSettingsDialogProps = {
        open: projectSettings.isOpen,
        onClose: () => setProjectSettings({ isOpen: false }),
    };
    const projectSettingsProps = {
        isOpen: projectSettings.isOpen,
        project: getProject(activeCourse),
        updateProjectSettings,
        onClose: () => setProjectSettings({ isOpen: false }),
    };

    const responsiveProjectSettings = {
        mobile: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'mobile'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
        tablet: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'tablet'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
        desktop: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'desktop'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
    };

    if (isLoading) return <LogoLoading style={{ marginTop: '30vh', height: 'fit-content' }} />;

    return (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveProjectSettings)}
            <SyncRosterDialog
                loading={isSyncRosterLoading}
                open={syncRosterDialog.open}
                isGroupSyncing={false}
                onClose={() => setSyncRosterDialog({ open: false, data: null })}
            />
            <AlertDialog
                message={dialog.message}
                description={dialog.description}
                open={dialog.open}
                variant={dialog.variant}
                actionButtonText={dialog.actionButtonText}
                onAction={dialog.onAction}
                onCancel={() => setDialog((current) => ({ ...current, open: false }))}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
            />
            <CloningContents
                open={cloningContentsDialog.open}
                setOpen={setCloningContentsDialog}
                course={getActiveCourse(activeCourse)}
                dialog={dialog}
                setDialog={setDialog}
                generatedIdList={generatedIdList}
                setGeneratedIdList={setGeneratedIdList}
            />
        </>
    );
};

export default PageLayout;
